import React, { useMemo, useState } from 'react';
import Style from './style.module.css';

const NEWS_DISPLAY_LIMIT = 200;

const EMPTY_SUMMARY = {
  flatRowCount: 0,
  incidentCount: 0,
  linkedCount: 0,
  unlinkedCount: 0,
  articleCount: 0,
};

const SORT_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'victimCount', label: 'Victims count' },
  { value: 'articleCount', label: 'Number articles' },
  { value: 'nearestSchoolBlocks', label: 'School distance (blocks)' },
  { value: 'nearestSchool', label: 'Nearest school' },
  { value: 'neighborhood', label: 'Neighborhood' },
  { value: 'zipCode', label: 'Zip code' },
  { value: 'previewTitle', label: 'Title' },
  { value: 'linkStatus', label: 'Incident Matched' },
];

export const parseArticlesJson = (raw) => {
  if (!raw?.trim()) {
    return { summary: EMPTY_SUMMARY, incidents: [] };
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      summary: { ...EMPTY_SUMMARY, ...(parsed.summary ?? {}) },
      incidents: Array.isArray(parsed.incidents) ? parsed.incidents : [],
    };
  } catch {
    return { summary: EMPTY_SUMMARY, incidents: [] };
  }
};

const formatArticleUrl = (raw) =>
  /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

const formatCell = (value) => {
  if (value == null || value === '') {
    return '-';
  }
  return String(value);
};

const formatVictimCountLabel = (victimCount) => {
  if (victimCount == null) {
    return 'Victims count: unknown';
  }
  return `Victims count: ${victimCount}`;
};

const formatArticleCountLabel = (articleCount) =>
  `Number articles: ${articleCount ?? 0}`;

const formatCouncilDistrictLabel = (councilDistrict) => {
  if (councilDistrict == null || councilDistrict === '') {
    return null;
  }

  const formatted = formatDcKey(councilDistrict);
  return formatted ? `Council district ${formatted}` : null;
};

const formatLinkStatusLabel = (linkStatus, dcKey) => {
  if (linkStatus === 'linked' && dcKey) {
    return `Incident matched · key ${formatDcKey(dcKey)}`;
  }
  return 'Incident unmatched';
};

/** Strip trailing decimal zeros from numeric DC keys (e.g. 202635063568.00000000). */
export const formatDcKey = (raw) => {
  if (raw == null || raw === '') {
    return null;
  }

  const text = String(raw).trim();
  const num = Number(text);

  if (!Number.isFinite(num)) {
    return text;
  }

  return String(num);
};

/** Convert ISO date text (YYYY-MM-DD) to e.g. 12/13/2025 */
export const formatDisplayDate = (raw) => {
  if (raw == null || raw === '') {
    return '-';
  }

  const text = String(raw).trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return text;
  }

  const [, year, month, day] = match;
  return `${Number(month)}/${Number(day)}/${year}`;
};

/** Convert 24h time or timestamp text to e.g. 1:05am, 11:00pm */
export const formatDisplayTime = (raw) => {
  if (raw == null || raw === '') {
    return null;
  }

  const text = String(raw).trim();
  const fullTsMatch = text.match(
    /\d{4}-\d{2}-\d{2}[ T](\d{1,2}):(\d{2})(?::\d{2})?/,
  );
  const timeOnlyMatch = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);

  let hours;
  let minutes;

  if (fullTsMatch) {
    hours = Number.parseInt(fullTsMatch[1], 10);
    minutes = fullTsMatch[2];
  } else if (timeOnlyMatch) {
    hours = Number.parseInt(timeOnlyMatch[1], 10);
    minutes = timeOnlyMatch[2];
  } else {
    return text;
  }

  if (Number.isNaN(hours) || hours < 0 || hours > 23) {
    return text;
  }

  const period = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes}${period}`;
};

const compareNullable = (left, right, direction) => {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }

  if (typeof left === 'number' && typeof right === 'number') {
    return (left - right) * direction;
  }

  return (
    String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: 'base',
    }) * direction
  );
};

const getSortValue = (incident, sortField) => {
  switch (sortField) {
    case 'date':
      return incident.date ?? null;
    case 'victimCount':
      return incident.victimCount ?? null;
    case 'articleCount':
      return incident.articleCount ?? 0;
    case 'nearestSchoolBlocks': {
      const blocks = incident.nearestSchoolBlocks;
      if (blocks == null || blocks === '') {
        return null;
      }
      const parsed = Number(blocks);
      return Number.isNaN(parsed) ? null : parsed;
    }
    case 'nearestSchool':
      return incident.nearestSchool?.toLowerCase() ?? null;
    case 'neighborhood':
      return incident.neighborhood?.toLowerCase() ?? null;
    case 'zipCode':
      return incident.zipCode?.toLowerCase() ?? null;
    case 'previewTitle':
      return incident.previewTitle?.toLowerCase() ?? null;
    case 'linkStatus':
      return incident.linkStatus === 'linked' ? 0 : 1;
    default:
      return incident.date ?? null;
  }
};

const sortIncidents = (incidents, sortField, sortDirection) => {
  const direction = sortDirection === 'asc' ? 1 : -1;
  const copy = [...incidents];

  copy.sort((left, right) => {
    const primary = compareNullable(
      getSortValue(left, sortField),
      getSortValue(right, sortField),
      direction,
    );

    if (primary !== 0) {
      return primary;
    }

    if (sortField === 'date') {
      return compareNullable(
        left.groupKey ?? null,
        right.groupKey ?? null,
        direction,
      );
    }

    return compareNullable(left.date ?? null, right.date ?? null, -1);
  });

  return copy;
};

const IncidentDetail = ({ incident }) => {
  const locationParts = [
    incident.locationText,
    incident.neighborhood,
    incident.zipCode,
  ].filter(Boolean);
  const displayTime = formatDisplayTime(incident.time);
  const councilDistrictLabel = formatCouncilDistrictLabel(
    incident.councilDistrict,
  );

  return (
    <div className={Style.incidentDetailCard}>
      <div className={Style.incidentDetailHeader}>
        <div>
          <p className={Style.incidentDetailDate}>
            {formatDisplayDate(incident.date)}
          </p>
          {displayTime ? (
            <p className={Style.incidentDetailSubtext}>{displayTime}</p>
          ) : null}
        </div>
        {incident.linkStatus === 'linked' && incident.dcKey ? (
          <span className={Style.badgeLinked}>
            {formatLinkStatusLabel(incident.linkStatus, incident.dcKey)}
          </span>
        ) : (
          <span className={Style.badgeUnlinked}>
            {formatLinkStatusLabel(incident.linkStatus, incident.dcKey)}
          </span>
        )}
      </div>

      <div className={Style.incidentDetailSection}>
        <h5 className={Style.incidentDetailSectionTitle}>Location</h5>
        <p className={Style.incidentDetailText}>
          {locationParts.length ? locationParts.join(' · ') : '-'}
        </p>
      </div>

      <div className={Style.incidentDetailSection}>
        <h5 className={Style.incidentDetailSectionTitle}>Details</h5>
        <p className={Style.incidentDetailText}>
          {formatVictimCountLabel(incident.victimCount)} ·{' '}
          {formatArticleCountLabel(incident.articleCount)}
          {incident.victimCount != null && incident.victimCount > 1
            ? ' · Multi-victim'
            : ''}
        </p>
        {incident.nearestSchool ? (
          <p className={Style.incidentDetailSubtext}>
            Nearest school: {incident.nearestSchool}
            {incident.nearestSchoolBlocks != null
              ? ` · ${incident.nearestSchoolBlocks} blocks`
              : ''}
          </p>
        ) : null}
        {councilDistrictLabel ? (
          <p className={Style.incidentDetailSubtext}>{councilDistrictLabel}</p>
        ) : null}
      </div>

      <div className={Style.incidentDetailSection}>
        <h5 className={Style.incidentDetailSectionTitle}>Victims</h5>
        {incident.victims.length > 0 ? (
          <table className={Style.incidentVictimsTable}>
            <thead>
              <tr>
                <th>Sex</th>
                <th>Race</th>
                <th>Age</th>
                <th>Latino</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {incident.victims.map((victim, index) => (
                <tr key={`${incident.groupKey}-victim-${index}`}>
                  <td>{formatCell(victim.sex)}</td>
                  <td>{formatCell(victim.race)}</td>
                  <td>{formatCell(victim.age)}</td>
                  <td>{formatCell(victim.latino)}</td>
                  <td>{formatCell(victim.fatal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className={Style.incidentDetailSubtext}>
            No public victim record matched.
          </p>
        )}
      </div>

      <div className={Style.incidentDetailSection}>
        <h5 className={Style.incidentDetailSectionTitle}>News coverage</h5>
        <ul className={Style.incidentArticlesList}>
          {incident.articles.map((article, index) => {
            const title = article.title || 'Untitled article';
            const url = article.url?.trim();

            return (
              <li
                key={`${incident.groupKey}-article-${index}`}
                className={Style.incidentArticleItem}
              >
                {url ? (
                  <a
                    className={Style.incidentArticleLink}
                    href={formatArticleUrl(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={title}
                  >
                    {title}
                  </a>
                ) : (
                  <span className={Style.incidentDetailText}>{title}</span>
                )}
                <p className={Style.incidentArticleSubtext}>
                  {[
                    article.publishedAt
                      ? `Published ${formatDisplayDate(
                          String(article.publishedAt).slice(0, 10),
                        )}`
                      : null,
                    `Author: ${formatCell(article.author)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const IncidentPreviewRow = ({ incident, isExpanded, onToggle }) => {
  const previewMetaParts = [
    incident.neighborhood,
    incident.zipCode,
    formatVictimCountLabel(incident.victimCount),
    formatArticleCountLabel(incident.articleCount),
    formatLinkStatusLabel(incident.linkStatus, incident.dcKey),
  ].filter(Boolean);
  const displayTime = formatDisplayTime(incident.time);

  return (
    <div>
      <div
        className={`${Style.incidentPreviewRow} ${
          isExpanded ? Style.incidentPreviewRowExpanded : ''
        }`}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <button
          type="button"
          className={Style.incidentExpandButton}
          aria-label={isExpanded ? 'Collapse incident' : 'Expand incident'}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        >
          {isExpanded ? '▾' : '▸'}
        </button>

        <div className={Style.incidentPreviewMain}>
          <p
            className={Style.incidentPreviewTitle}
            title={incident.previewTitle}
          >
            {incident.previewTitle || 'Untitled article'}
          </p>
          <p className={Style.incidentPreviewMeta}>
            {previewMetaParts.join(' · ')}
          </p>
        </div>

        <div className={Style.incidentPreviewDateBlock}>
          <span className={Style.incidentPreviewDate}>
            {formatDisplayDate(incident.date)}
          </span>
          {displayTime ? (
            <span className={Style.incidentPreviewTime}>{displayTime}</span>
          ) : null}
        </div>
      </div>

      {isExpanded ? <IncidentDetail incident={incident} /> : null}
    </div>
  );
};

const ArticlesIncidentList = ({ articlesPayload }) => {
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const { summary, incidents } = articlesPayload;

  const sortedIncidents = useMemo(
    () => sortIncidents(incidents, sortField, sortDirection),
    [incidents, sortField, sortDirection],
  );
  const visibleIncidents = sortedIncidents.slice(0, NEWS_DISPLAY_LIMIT);
  const hasMoreIncidents = sortedIncidents.length > NEWS_DISPLAY_LIMIT;

  const toggleIncident = (groupKey) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  if (!incidents.length) {
    return (
      <div className={Style.emptyState}>
        No related articles for this report.
      </div>
    );
  }

  return (
    <div className={Style.rawDataCard}>
      <div className={Style.rawDataHeader}>
        <div>
          <h4 className={Style.rawDataTitle}>
            Related articles (experimental)
          </h4>
          <p className={Style.rawDataMeta}>
            {summary.incidentCount} incidents · {summary.linkedCount} linked ·{' '}
            {summary.unlinkedCount} unlinked · Number articles:{' '}
            {summary.articleCount}
          </p>
        </div>
      </div>

      <div className={Style.incidentSortBar}>
        <label
          className={Style.incidentSortLabel}
          htmlFor="incident-sort-field"
        >
          Sort by
        </label>
        <select
          id="incident-sort-field"
          className={Style.incidentSortSelect}
          value={sortField}
          onChange={(event) => setSortField(event.target.value)}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          id="incident-sort-direction"
          className={Style.incidentSortSelect}
          value={sortDirection}
          onChange={(event) => setSortDirection(event.target.value)}
          aria-label="Sort direction"
        >
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </select>
      </div>

      {hasMoreIncidents && (
        <div className={Style.emptyState}>
          News preview is limited to {NEWS_DISPLAY_LIMIT} incidents. Use the CSV
          export for the full dataset.
        </div>
      )}

      <div className={Style.articlesIncidentList}>
        {visibleIncidents.map((incident) => (
          <IncidentPreviewRow
            key={incident.groupKey}
            incident={incident}
            isExpanded={expandedKeys.has(incident.groupKey)}
            onToggle={() => toggleIncident(incident.groupKey)}
          />
        ))}
      </div>
    </div>
  );
};

export default ArticlesIncidentList;
